import { Delete, Edit, Visibility } from "@mui/icons-material";
import { Chip, Tooltip } from "@mui/material";
import moment from "moment";

import { TableActions } from "../../components/v3";

/**
 * Route paths for the super-admin companies module, mounted under `/companies`.
 * Single source of truth — reuse these instead of hard-coding `/companies...` strings.
 * The child paths (other than `root`) are relative to the `/companies` wrapper route.
 */
export const SUPERADMIN_ROUTES = {
  root: "/companies",
  details: ":id",
  addAdmin: ":id/add-admin",
  editAdmin: ":id/edit-admin/:adminId",
  addPlan: ":id/add-plan",
  editPlan: ":id/edit-plan/:planId",
  add: "add",
  edit: "edit/:id",
} as const;

export const getCompanyTableColumns = (handleActions) => [
  { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
  { field: "createdAt", headerName: "created Date", flex: 1, sortable: true },
  { field: "name", headerName: "company name", flex: 1.5, sortable: true },
  { field: "primaryPhone", headerName: "phone", flex: 1, sortable: true },
  { field: "admin", headerName: "admin", flex: 1.5, sortable: false },
  { field: "place", headerName: "place", flex: 1, sortable: false },
  {
    field: "planDetails",
    headerName: "Plan",
    flex: 2,
    sortable: false,
    renderCell: (params) => {
      const plan = params.row.planDetails;
      if (!plan) return <Chip label="No Plan" color="default" size="small" />;
      const { name, type, price, billingCycle, startDate, endDate, active } = plan;
      const formattedStartDate = startDate ? moment(startDate).format("DD MMM YYYY") : "N/A";
      const formattedEndDate = endDate ? moment(endDate).format("DD MMM YYYY") : "N/A";
      const isExpired = endDate ? moment().isAfter(moment(endDate)) : true;
      const isExpiringSoon = !isExpired && moment(endDate).diff(moment(), "days") <= 10;

      return (
        <Tooltip
          title={`Name: ${name}\nType: ${type}\nPrice: ₹${price}\nCycle: ${billingCycle}\nValid: ${formattedStartDate} - ${formattedEndDate}`}
        >
          <Chip
            label={`₹${price} | ${billingCycle} | ${active ? "Active" : "Inactive"}
            | ${isExpired ? `Expired ${moment(endDate).fromNow(true)} ago` : `Expires ${moment(endDate).fromNow()}`}`}
            color={isExpired ? "error" : isExpiringSoon ? "warning" : active ? "success" : "warning"}
            size="small"
          />
        </Tooltip>
      );
    },
  },
  // {
  //   field: "actions",
  //   headerName: "Action",
  //   sortable: false,
  //   flex: 0.5,
  //   renderCell: (params) => (
  //     <TableActions
  //       item={params}
  //       actions={[
  //         { icon: Edit, type: "edit", label: "Edit", onClick: handleActions },
  //         {
  //           icon: Delete,
  //           type: "delete",
  //           label: "Delete",
  //           onClick: handleActions,
  //         },
  //       ]}
  //     />
  //   ),
  // },
];

export const getAdminTableColumns = (handleActions) => [
  { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
  { field: "firstName", headerName: "name", flex: 1.5, sortable: true },
  { field: "username", headerName: "user name", flex: 1.5, sortable: true },
  { field: "companyName", headerName: "company name", flex: 1.5, sortable: false },
  { field: "primaryPhone", headerName: "phone", flex: 1, sortable: false },
  { field: "createdAt", headerName: "joined at", flex: 1, sortable: true },
  { field: "place", headerName: "place", flex: 1.5, sortable: false },
  {
    field: "actions",
    headerName: "Action",
    sortable: false,
    flex: 1,
    renderCell: (params) => (
      <TableActions
        item={params}
        actions={[
          { icon: Edit, type: "edit", label: "Edit", onClick: handleActions },
          {
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          },
        ]}
      />
    ),
  },
];

export const getUserTableColumns = (handleActions) => [
  { field: "sl", headerName: "sl no", flex: 0.75, sortable: false },
  { field: "firstName", headerName: "first name", flex: 1.5, sortable: true },
  { field: "lastName", headerName: "last name", flex: 1.5, sortable: true },
  { field: "username", headerName: "username", flex: 1.5, sortable: true },
  { field: "type", headerName: "type", flex: 1.2, sortable: true },
  { field: "primaryPhone", headerName: "phone", flex: 1.5, sortable: false },
];

const ACTION_CHIP_COLOR = {
  CREATE: "success",
  UPDATE: "warning",
  DELETE: "error",
};

export const getCompanyActivityTableColumns = (handleView) => [
  { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
  {
    field: "createdAt",
    headerName: "Date",
    flex: 1.2,
    sortable: true,
  },
  {
    field: "entityType",
    headerName: "Entity Type",
    flex: 1.2,
    sortable: true,
    renderCell: (params) => <Chip label={params.value || "—"} size="small" color="primary" variant="outlined" />,
  },
  { field: "entityId", headerName: "Entity Id", flex: 0.8, sortable: true },
  {
    field: "action",
    headerName: "Action",
    flex: 1,
    sortable: true,
    renderCell: (params) => (
      <Chip label={params.value || "—"} size="small" color={ACTION_CHIP_COLOR[params.value] || "default"} variant="filled" />
    ),
  },
  { field: "performedBy", headerName: "Performed By", flex: 1.5, sortable: false },
  { field: "changesCount", headerName: "Changes", flex: 0.8, sortable: false },
  {
    field: "actions",
    headerName: "Action",
    sortable: false,
    flex: 0.5,
    renderCell: (params) => (
      <TableActions item={params} actions={[{ icon: Visibility, type: "view", label: "View", onClick: handleView }]} />
    ),
  },
];
